import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { StatefulButton } from "../../../components/ui/muiButtons";
import { useAccountContext } from "../../../strict/app/AccountResolver";
import useAuth from "../../../context/UserContext";
import {
  AccountsForLoggedUserDocument,
  AccountsForLoggedUserQuery,
  AccountsForLoggedUserQueryVariables,
  DeactivateAccountV2Document,
  DeactivateAccountV2Mutation,
  DeactivateAccountV2MutationVariables,
} from "../../../graphql/types";

interface IDeactivateAccount {
  showModalDeactivate: boolean;
  setShowModalDeactivate: (showModalDeactivate: boolean) => void;
  accountName: string;
  accountUrlName: string;
}

const DeactivateAccount = ({
  showModalDeactivate,
  setShowModalDeactivate,
  accountName,
  accountUrlName,
}: IDeactivateAccount) => {
  const navigate = useNavigate();
  const { urlName: ctxUrlName } = useAccountContext();
  const { updateAccounts } = useAuth();
  const [accountsForLoggedUser] = useLazyQuery<AccountsForLoggedUserQuery, AccountsForLoggedUserQueryVariables>(
    AccountsForLoggedUserDocument,
    { fetchPolicy: "network-only" }
  );
  const [deactivateAccount, deactivateAccountState] = useMutation<
    DeactivateAccountV2Mutation,
    DeactivateAccountV2MutationVariables
  >(DeactivateAccountV2Document, {
    onCompleted: async () => {
      try {
        const { data } = await accountsForLoggedUser({ context: { skipAccountUri: true } });
        const accounts =
          data?.meV2?.accounts.map((account) => {
            return {
              urlName: account.urlName,
              name: account.name,
              show_download_summary: Boolean(account.show_download_summary),
            };
          }) || [];

        updateAccounts(accounts);

        if (accountUrlName === ctxUrlName && accounts.length > 0) {
          localStorage.setItem("urlName", accounts[0].urlName);
        }

        navigate("/admin?currentTab=accounts");

        toast.success("Account deactivated");
      } catch {
        toast.error("Error when deactivating account");
      }
    },
    onError: () => {
      toast.error("Error when deactivating account");
    },
  });
  const handleDeactivateAccount = () => {
    deactivateAccount({
      variables: {
        urlName: accountUrlName,
      },
    });
  };

  const {
    control,
    formState: { isValid },
    reset,
  } = useForm<{
    deactivateField: string;
  }>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      deactivateField: "",
    },
  });
  return (
    <form>
      <Dialog
        open={showModalDeactivate}
        data-testid="confirm-delete-dialog"
        onClose={() => {
          setShowModalDeactivate(false);
          reset();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="heading.four">
          Deactivate <b>{accountName}</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "14px" }}>
            <b>This action cannot be undone.</b>
            <br />
            To confirm, please type <b>deactivate </b> below
          </DialogContentText>
          <Controller
            name="deactivateField"
            control={control}
            rules={{
              required: true,
              validate: (value) => value.toLocaleLowerCase() === "deactivate",
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                error={Boolean(fieldState.error)}
                required
                id="deactivateField"
                type="text"
                autoFocus
                data-testid="deactivate-field"
                helperText={fieldState.error?.message}
                autoComplete="off"
                margin="dense"
                placeholder="Type deactivate"
                fullWidth
                variant="standard"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModalDeactivate(false)} data-testid="cancel-button" variant="outlined">
            Cancel
          </Button>
          <StatefulButton
            onClick={handleDeactivateAccount}
            data-testid="confirm-button"
            loading={deactivateAccountState.loading}
            disabled={!isValid}
            color="error"
          >
            Confirm
          </StatefulButton>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export { DeactivateAccount };
